// ===== LOADING SPINNER COMPONENT =====
import React from "react";

/**
 * LoadingSpinner Component
 * Displays a loading spinner with optional text and different sizes
 *
 * @param {Object} props - Component props
 * @param {string} props.size - Size of spinner ('sm', 'md', 'lg', 'xl')
 * @param {string} props.text - Optional loading text
 * @param {string} props.variant - Bootstrap color variant
 * @param {boolean} props.center - Whether to center the spinner
 * @param {string} props.className - Additional CSS classes
 * @param {React.CSSProperties} props.style - Inline styles
 * @param {boolean} props.overlay - Show as full-screen overlay
 * @param {string} props.type - Type of spinner ('border', 'grow')
 */
export const LoadingSpinner = ({
  size = "md",
  text = null,
  variant = "primary",
  center = false,
  className = "",
  style = {},
  overlay = false,
  type = "border",
}) => {
  // Size mappings
  const sizeClasses = {
    sm: "spinner-border-sm",
    md: "",
    lg: "spinner-border-lg",
    xl: "spinner-border-xl",
  };

  // Spinner class based on type
  const spinnerClass = type === "grow" ? "spinner-grow" : "spinner-border";

  // Build CSS classes
  const spinnerClasses = [
    spinnerClass,
    sizeClasses[size],
    `text-${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Container classes
  const containerClasses = [
    center && "d-flex justify-content-center align-items-center",
    overlay && "loading-overlay",
  ]
    .filter(Boolean)
    .join(" ");

  // Overlay styles
  const overlayStyles = overlay
    ? {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        zIndex: 9999,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        ...style,
      }
    : style;

  const content = (
    <>
      <div
        className={spinnerClasses}
        role="status"
        aria-hidden="true"
        style={overlay ? {} : style}
      >
        <span className="visually-hidden">Loading...</span>
      </div>
      {text && (
        <div
          className={`${text && !overlay ? "ms-2" : "mt-3"} text-${variant}`}
        >
          {text}
        </div>
      )}
    </>
  );

  if (overlay) {
    return (
      <div style={overlayStyles} className={className}>
        {content}
      </div>
    );
  }

  if (center || text) {
    return (
      <div className={containerClasses} style={overlayStyles}>
        {content}
      </div>
    );
  }

  return content;
};

/**
 * Inline Loading Spinner - for use within components
 */
export const InlineSpinner = ({
  size = "sm",
  variant = "primary",
  className = "",
  ...props
}) => {
  return (
    <LoadingSpinner
      size={size}
      variant={variant}
      className={`d-inline-block ${className}`}
      {...props}
    />
  );
};

/**
 * Button Loading Spinner - for use in buttons
 */
export const ButtonSpinner = ({
  size = "sm",
  variant = "light",
  className = "",
  ...props
}) => {
  return (
    <LoadingSpinner
      size={size}
      variant={variant}
      className={`me-2 ${className}`}
      {...props}
    />
  );
};

/**
 * Full Page Loading Spinner
 */
export const FullPageSpinner = ({
  text = "Loading...",
  variant = "primary",
  size = "lg",
  ...props
}) => {
  return (
    <div
      className="d-flex justify-content-center align-items-center vh-100"
      style={{ minHeight: "400px" }}
    >
      <div className="text-center">
        <LoadingSpinner size={size} variant={variant} {...props} />
        {text && <p className={`mt-3 text-${variant} mb-0`}>{text}</p>}
      </div>
    </div>
  );
};

/**
 * Card Loading Spinner - for loading states in cards
 */
export const CardSpinner = ({
  height = "200px",
  text = "Loading content...",
  variant = "primary",
  ...props
}) => {
  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ height, minHeight: "100px" }}
    >
      <div className="text-center">
        <LoadingSpinner variant={variant} {...props} />
        {text && <p className={`mt-3 text-muted small mb-0`}>{text}</p>}
      </div>
    </div>
  );
};

/**
 * Table Loading Spinner - for loading states in tables
 */
export const TableSpinner = ({
  colspan = 5,
  text = "Loading data...",
  variant = "primary",
  ...props
}) => {
  return (
    <tr>
      <td colSpan={colspan} className="text-center py-5">
        <LoadingSpinner variant={variant} text={text} center {...props} />
      </td>
    </tr>
  );
};

/**
 * Skeleton Loading Component - alternative to spinners
 */
export const SkeletonLoader = ({
  width = "100%",
  height = "20px",
  className = "",
  count = 1,
  style = {},
}) => {
  const skeletons = Array.from({ length: count }, (_, index) => (
    <div
      key={index}
      className={`placeholder-glow ${className}`}
      style={{
        width,
        height,
        marginBottom: count > 1 ? "10px" : "0",
        ...style,
      }}
    >
      <span className="placeholder w-100 h-100"></span>
    </div>
  ));

  return count > 1 ? <div>{skeletons}</div> : skeletons[0];
};

/**
 * Pulsing Dot Loader
 */
export const PulsingDots = ({
  variant = "primary",
  size = "md",
  className = "",
}) => {
  const sizeMap = {
    sm: "6px",
    md: "8px",
    lg: "10px",
  };

  const dotSize = sizeMap[size];

  return (
    <div className={`pulsing-dots ${className}`}>
      <style jsx>{`
        .pulsing-dots {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .dot {
          width: ${dotSize};
          height: ${dotSize};
          border-radius: 50%;
          animation: pulse 1.4s ease-in-out infinite both;
        }

        .dot:nth-child(1) {
          animation-delay: -0.32s;
        }
        .dot:nth-child(2) {
          animation-delay: -0.16s;
        }
        .dot:nth-child(3) {
          animation-delay: 0s;
        }

        @keyframes pulse {
          0%,
          80%,
          100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
      <div className={`dot bg-${variant}`}></div>
      <div className={`dot bg-${variant}`}></div>
      <div className={`dot bg-${variant}`}></div>
    </div>
  );
};

/**
 * Progress Bar Loader
 */
export const ProgressLoader = ({
  progress = 0,
  variant = "primary",
  showPercentage = true,
  height = "4px",
  className = "",
  animated = false,
  striped = false,
}) => {
  return (
    <div className={`progress ${className}`} style={{ height }}>
      <div
        className={`progress-bar bg-${variant} ${
          animated ? "progress-bar-animated" : ""
        } ${striped ? "progress-bar-striped" : ""}`}
        role="progressbar"
        style={{ width: `${progress}%` }}
        aria-valuenow={progress}
        aria-valuemin="0"
        aria-valuemax="100"
      >
        {showPercentage && <span className="small">{progress}%</span>}
      </div>
    </div>
  );
};

/**
 * Loading Text Component
 */
export const LoadingText = ({
  text = "Loading",
  variant = "muted",
  animated = true,
  className = "",
}) => {
  return (
    <span className={`text-${variant} ${className}`}>
      {text}
      {animated && (
        <span className="loading-dots">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      )}
      <style jsx>{`
        .loading-dots span {
          animation: loading 1.4s ease-in-out infinite both;
        }
        .loading-dots span:nth-child(1) {
          animation-delay: -0.32s;
        }
        .loading-dots span:nth-child(2) {
          animation-delay: -0.16s;
        }
        .loading-dots span:nth-child(3) {
          animation-delay: 0s;
        }

        @keyframes loading {
          0%,
          80%,
          100% {
            opacity: 0;
          }
          40% {
            opacity: 1;
          }
        }
      `}</style>
    </span>
  );
};

// Export default
export default LoadingSpinner;
